/**
 * Subscription state pages + read-only banner (Feb 2026).
 *
 * Rendered when the backend `/api/subscription/access` reports a non-full
 * access mode. Only login / logout / these pages / billing / checkout are
 * reachable in blocked states.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { CreditCard, AlertTriangle, Archive, LogOut } from 'lucide-react';


function Shell({ icon: Icon, title, subtitle, children, testId }) {
  const { logout } = useAuth();
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4"
      data-testid={testId}
    >
      <div className="max-w-lg w-full bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#1A3E6F]/8 mx-auto mb-4">
          <Icon className="w-6 h-6 text-[#1A3E6F]" />
        </div>
        <h1 className="text-2xl font-bold text-[#0F172A] text-center">{title}</h1>
        <p className="text-sm text-[#64748B] text-center mt-2">{subtitle}</p>
        <div className="mt-6">{children}</div>
        <div className="mt-6 pt-6 border-t border-[#E2E8F0] flex items-center justify-center">
          <Button
            variant="ghost"
            onClick={() => logout()}
            className="text-xs text-[#64748B] hover:text-[#0F172A] gap-1.5"
            data-testid="subscription-page-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Log out
          </Button>
        </div>
      </div>
    </div>
  );
}


// pending_payment | payment_processing | (no subscription record)
// Sept 2026 — when there is a `pending_subscription_intent` stashed by
// PricingPage (the user chose a plan before signup), the customer has
// already picked their product. Showing "Choose a plan" alongside
// "Resume checkout" confused newly-verified users. We now show ONLY
// the button that matches their state.
export function CompleteSubscriptionPage() {
  const nav = useNavigate();
  const { access } = useSubscriptionAccess();
  const status = access?.status;
  const hasPendingIntent = (() => {
    try { return !!localStorage.getItem('pending_subscription_intent'); }
    catch { return false; }
  })();

  return (
    <Shell
      icon={CreditCard}
      title="Complete your subscription"
      subtitle={
        status === 'payment_processing'
          ? 'Your payment is being processed. Access will unlock as soon as it completes.'
          : hasPendingIntent
            ? 'You already selected a plan before verifying your email. Resume your checkout to complete payment.'
            : 'You need an active subscription to use the VOX platform. Complete your payment to unlock full access.'
      }
      testId="page-complete-subscription"
    >
      <div className="space-y-2">
        {hasPendingIntent ? (
          <Button
            onClick={() => nav('/pricing')}
            className="w-full h-11 bg-[#1A3E6F] hover:bg-[#15325A] text-white text-sm"
            data-testid="complete-subscription-resume-checkout"
          >
            Resume checkout
          </Button>
        ) : (
          <Button
            onClick={() => nav('/pricing')}
            className="w-full h-11 bg-[#1A3E6F] hover:bg-[#15325A] text-white text-sm"
            data-testid="complete-subscription-choose-plan"
          >
            Choose a plan
          </Button>
        )}
      </div>
      <p className="text-[11px] text-[#94A3B8] text-center mt-4">
        Already paid by bank transfer? Contact support to have your subscription activated.
      </p>
    </Shell>
  );
}


// past_due — allowed to reach billing/payment issue page + logout.
export function BillingIssuePage() {
  const nav = useNavigate();
  return (
    <Shell
      icon={AlertTriangle}
      title="Billing issue on your account"
      subtitle="Your subscription is past due. Resolve payment to restore full access — you can still view existing surveys and reports until then."
      testId="page-billing-issue"
    >
      <Button
        onClick={() => nav('/pricing')}
        className="w-full h-11 bg-[#1A3E6F] hover:bg-[#15325A] text-white text-sm"
        data-testid="billing-issue-update-payment"
      >
        Update payment
      </Button>
      <Button
        variant="outline"
        onClick={() => nav('/dashboard')}
        className="w-full h-11 mt-2 border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] text-sm"
        data-testid="billing-issue-continue"
      >
        Continue in read-only mode
      </Button>
    </Shell>
  );
}


// archived (or expired/cancelled past grace).
export function SubscriptionArchivedPage() {
  return (
    <Shell
      icon={Archive}
      title="This account is archived"
      subtitle="Your subscription has ended and the read-only grace period has passed. Your data is preserved — contact support to reactivate your subscription."
      testId="page-subscription-archived"
    >
      <a
        href="mailto:support@satisfind.com"
        className="block w-full text-center h-11 leading-[2.75rem] rounded-md bg-[#1A3E6F] hover:bg-[#15325A] text-white text-sm font-medium"
        data-testid="archived-contact-support"
      >
        Contact support
      </a>
    </Shell>
  );
}


// Compact banner rendered inside DashboardLayout when the tenant is in
// read-only or past-due mode. Non-dismissible so users can't lose sight of
// the limitation, but keeps the app usable.
export function SubscriptionAccessBanner() {
  const { access, isReadOnly, isPastDue } = useSubscriptionAccess();
  const nav = useNavigate();
  if (!isReadOnly && !isPastDue) return null;
  const status = access?.status || (isPastDue ? 'past_due' : 'read_only');
  const isPD = isPastDue;
  return (
    <div
      className={`rounded-lg border px-4 py-3 mb-4 flex items-start gap-3 ${
        isPD
          ? 'bg-[#FEF2F2] border-[#FCA5A5]'
          : 'bg-[#FEFCE8] border-[#FDE68A]'
      }`}
      data-testid={`subscription-banner-${status}`}
    >
      <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isPD ? 'text-[#B91C1C]' : 'text-[#92400E]'}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold ${isPD ? 'text-[#B91C1C]' : 'text-[#92400E]'}`}>
          {isPD ? 'Billing issue — payment past due' : 'Read-only grace period'}
        </p>
        <p className={`text-[11px] mt-0.5 ${isPD ? 'text-[#7F1D1D]' : 'text-[#78350F]'}`}>
          {isPD
            ? 'Your subscription is past due. Existing data is available for viewing, but you cannot create, edit, publish, or collect new responses until the billing issue is resolved.'
            : `Your subscription has ended. You can still view existing surveys, responses and reports${access?.read_only_until ? ` until ${access.read_only_until}` : ''}. Renew to unlock write access.`
          }
        </p>
      </div>
      <button
        onClick={() => nav(isPD ? '/billing-issue' : '/pricing')}
        className={`text-[11px] font-semibold underline flex-shrink-0 ${
          isPD ? 'text-[#B91C1C] hover:text-[#7F1D1D]' : 'text-[#92400E] hover:text-[#78350F]'
        }`}
        data-testid="subscription-banner-cta"
      >
        {isPD ? 'Resolve' : 'Renew'}
      </button>
    </div>
  );
}
